import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Card, Spinner } from 'react-bootstrap';
import { CardEmployee, StyledCardTitleEmployee } from '../../pages/welcome/styles';
import { useQuestions } from '../../context/QuestionContext';
import questions from '../../data/questions-ibfoz-jan26.json';
import RenderField from '../renderField';
import { useAuth } from '../../context/AuthContext';
import { initAnswers } from '../../services/question/question';
import { useToast } from '../../context/Toast';
import { BackButton } from '../renderField/styles';
import { useNavigate } from 'react-router-dom';
import { getUserByCellphone, getUserByEmail } from '../../services/user/user';
import { browserSession } from '../../services/utils/browserSession';
import { useTranslation } from 'react-i18next';

const buildAppError = (name: string, message?: string) => {
  const error = new Error(message || name);
  error.name = name;
  return error;
};

const QuestionCard: React.FC = () => {
  const [flagStatus, setFlagStatus] = useState(false);
  const [loading, setLoading] = useState(false);

  const { showToast } = useToast();
  const { t } = useTranslation('questions');
  const { user } = useAuth();
  const {
    currentQuestionIndex,
    setCurrentQuestionIndex,
    answerId,
    saveDataAnswerOnStorage,
  } = useQuestions();

  const navigate = useNavigate();

  const captureQuestionCardIssue = (
    reason: string,
    extra?: Record<string, unknown>,
    originalError?: unknown
  ) => {
    Sentry.withScope((scope) => {
      scope.setTag('module', 'question_card');
      scope.setTag('issue_reason', reason);
      scope.setTag('page_path', window.location.pathname);
      scope.setTag('is_iframe', String(window.top !== window.self));

      scope.setContext('question_card_debug', {
        href: window.location.href,
        origin: window.location.origin,
        referrer: document.referrer,
        userAgent: navigator.userAgent,
        hasUserInAuthContext: !!user?.user && Object.keys(user.user || {}).length > 0,
        hasVerifyTokenInAuthContext: !!user?.verifyToken,
        hasAnswerIdInContext: !!answerId,
        localStorageAvailable: browserSession.isLocalStorageAvailable(),
        ...extra,
      });

      if (originalError instanceof Error) {
        Sentry.captureException(originalError);
      } else {
        Sentry.captureException(buildAppError(reason));
      }
    });
  };

  const getResolvedUser = () => {
    const authUser = user?.user;

    if (authUser && Object.keys(authUser).length > 0) {
      return authUser;
    }

    const storedIdentity = browserSession.getIdentity();
    const fallbackUser = storedIdentity?.user;

    if (fallbackUser && Object.keys(fallbackUser).length > 0) {
      return fallbackUser;
    }

    return null;
  };

  const initQuestion = async () => {
    setLoading(true);

    try {
      const resolvedUser = getResolvedUser();

      if (!resolvedUser) {
        captureQuestionCardIssue('user_not_available_for_question_start', {
          identityInMemory: !!user?.user && Object.keys(user.user || {}).length > 0,
          identityInBrowserSession:
            !!browserSession.getIdentity()?.user &&
            Object.keys(browserSession.getIdentity()?.user || {}).length > 0,
        });

        throw buildAppError('user_not_available_for_question_start');
      }

      let userDb = null;

      try {
        if (resolvedUser?.cellphone) {
          userDb = await getUserByCellphone(resolvedUser.cellphone);
        } else if (resolvedUser?.email) {
          userDb = await getUserByEmail(resolvedUser.email);
        }
      } catch (error) {
        captureQuestionCardIssue(
          'failed_to_fetch_user_before_question_start',
          {
            hasCellphone: !!resolvedUser?.cellphone,
            hasEmail: !!resolvedUser?.email,
            resolvedUserId: resolvedUser?.id ?? null,
          },
          error
        );
      }

      const userId = resolvedUser?.id || userDb?.id;
      const userStatus = userDb?.status;

      if (!userId) {
        captureQuestionCardIssue('user_id_not_resolved_for_question_start', {
          resolvedUserId: resolvedUser?.id ?? null,
          apiUserId: userDb?.id ?? null,
          hasEmail: !!resolvedUser?.email,
          hasCellphone: !!resolvedUser?.cellphone,
        });

        throw buildAppError('user_id_not_resolved_for_question_start');
      }

      // Mantém sua regra atual
      if (
        !resolvedUser?.cellphone &&
        userStatus != 1 &&
        resolvedUser?.email &&
        resolvedUser?.email !== '-'
      ) {
        throw buildAppError('user_not_active');
      }

      const dataToSend = {
        answer: {},
        user_id: userId,
      };

      const result = await initAnswers(dataToSend);

      if (!result?.id) {
        captureQuestionCardIssue('init_answers_returned_without_id', {
          userId,
        });

        throw buildAppError('init_answers_returned_without_id');
      }

      saveDataAnswerOnStorage(result.id);
      setCurrentQuestionIndex(0);
    } catch (error: any) {
      setCurrentQuestionIndex(null);

      if (error?.name === 'user_not_active') {
        setFlagStatus(true);
      } else {
        captureQuestionCardIssue(
          'question_card_init_failed',
          {
            errorName: error?.name ?? null,
            errorMessage: error?.message ?? null,
          },
          error
        );

        showToast(t('genericErrorDetailed'),
          'error',
          30000
        );

        throw error;
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!answerId) {
      initQuestion();
    } else {
      setCurrentQuestionIndex(0);
    }
  }, []);

  return (
    <>
      {loading ? (
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ minHeight: '300px' }}
        >
          <Spinner style={{ color: '#c9c9c9' }} />
        </div>
      ) : (
        <CardEmployee>
          <Card.Body>
            {currentQuestionIndex != null ? (
              <div>
                <RenderField entity={questions[currentQuestionIndex]} />
              </div>
            ) : flagStatus ? (
              <>
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{ minHeight: '300px' }}
                >
                  <StyledCardTitleEmployee>                    {t('completeRegistration')}
                  </StyledCardTitleEmployee>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginTop: 20,
                  }}
                >
                  <BackButton onClick={() => navigate('/register')}>                    {t('finishRegistration')}
                  </BackButton>
                </div>
              </>
            ) : (
              <>
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{ minHeight: '300px' }}
                >
                  <StyledCardTitleEmployee>                    {t('notAvailable')}
                  </StyledCardTitleEmployee>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginTop: 20,
                  }}
                >
                  <BackButton onClick={() => navigate('/welcome')}>                    {t('back')}
                  </BackButton>
                </div>
              </>
            )}
          </Card.Body>
        </CardEmployee>
      )}
    </>
  );
};

export default QuestionCard;
